import { useMemo, useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "@phosphor-icons/react";
import { MODELS, BUNDLES, PLUGS, getPricing } from "@/lib/products";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReservationDialog from "./ReservationDialog";

function OptionCard({ selected, onClick, testId, badge, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={selected}
      className={`relative overflow-hidden text-left w-full rounded-3xl border p-6 md:p-7 backdrop-blur-xl transition-all duration-300 group ${
        selected
          ? "border-teal-brand/60 ring-2 ring-teal-brand/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_0_40px_-10px_rgba(1,207,198,0.55)]"
          : "border-white/70 hover:-translate-y-0.5 hover:border-teal-brand/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_10px_30px_-18px_rgba(2,47,107,0.2)] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_20px_50px_-24px_rgba(2,47,107,0.28),0_0_24px_-8px_rgba(1,207,198,0.35)]"
      }`}
      style={{
        backgroundImage: selected
          ? "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(213,246,244,0.75) 100%)"
          : "linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(240,247,251,0.6) 100%)",
      }}
    >
      {/* Liquid highlight sheen */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-6 top-0 h-1/3 rounded-t-3xl bg-gradient-to-b from-white/70 to-transparent opacity-90"
      />
      {/* Specular hover sweep */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-y-2 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/60 to-transparent opacity-0 group-hover:opacity-100 group-hover:left-[110%] transition-all duration-700 ease-out"
      />
      {badge && (
        <span
          className={`absolute top-4 right-4 z-10 overline text-[9.5px] px-2.5 py-1 rounded-full ${
            badge === "Most Popular"
              ? "bg-signature-gradient text-white"
              : "bg-teal-brand/15 text-cyan-brand"
          }`}
        >
          {badge}
        </span>
      )}
      <div className="relative z-[1]">{children}</div>
      <div
        className={`relative z-[1] mt-5 inline-flex items-center gap-2 text-xs font-medium ${
          selected ? "text-cyan-brand" : "text-cool-grey"
        }`}
      >
        <span
          className={`h-4 w-4 rounded-full border grid place-items-center ${
            selected
              ? "bg-teal-brand border-teal-brand text-navy-deep"
              : "border-[#8A97A8]/50"
          }`}
        >
          {selected && <Check size={10} weight="bold" />}
        </span>
        {selected ? "Selected" : "Select"}
      </div>
    </button>
  );
}

function PlugSegmented({ value, onChange }) {
  const wrapRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const el = wrapRef.current?.querySelector(
      `[data-plug="${value}"]`,
    );
    if (el && wrapRef.current) {
      const parentRect = wrapRef.current.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      setIndicator({ left: r.left - parentRect.left, width: r.width });
    }
  }, [value]);

  return (
    <div
      ref={wrapRef}
      className="relative inline-flex items-center rounded-full bg-cool-white border border-[#8A97A8]/25 p-1"
    >
      <span
        className="absolute top-1 bottom-1 rounded-full bg-navy-deep transition-all duration-300 ease-out"
        style={{ left: indicator.left, width: indicator.width }}
      />
      {PLUGS.map((p) => {
        const active = p.id === value;
        return (
          <button
            key={p.id}
            data-plug={p.id}
            data-testid={`plug-${p.id}`}
            type="button"
            onClick={() => onChange(p.id)}
            className={`relative z-10 h-10 px-5 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
              active ? "text-white" : "text-navy-deep hover:text-cyan-brand"
            }`}
          >
            <span className="text-base leading-none">{p.flag}</span>
            <span>{p.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Configurator() {
  const [model, setModel] = useState("original");
  const [bundle, setBundle] = useState("bundle");
  const [plug, setPlug] = useState("US");
  const [plug2, setPlug2] = useState("US");
  const [open, setOpen] = useState(false);

  const bundleData = BUNDLES[bundle];
  const pricing = getPricing(model, bundle);
  const savings = useMemo(
    () => pricing.compareAt - pricing.price,
    [pricing],
  );

  return (
    <section
      id="configure"
      data-testid="configurator"
      className="scroll-mt-24 md:scroll-mt-28 lg:scroll-mt-32 py-14 sm:py-20 bg-cool-white"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="rounded-[28px] bg-white border border-[#8A97A8]/25 p-6 sm:p-10 lg:p-14 shadow-[0_30px_80px_-40px_rgba(2,47,107,0.25)]">
          <div className="max-w-2xl reveal">
            <div className="overline text-cyan-brand">Configure your CryoVault</div>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-deep tracking-tight leading-[1.05]">
              Two sizes. One promise.
            </h2>
            <p className="mt-4 text-slate-600 text-base max-w-lg">
              Engineered to keep your medication at a clinical 2–8°C, wherever
              life takes you.
            </p>
          </div>

          {/* Step A + B side by side on desktop */}
          <div className="mt-12 grid md:grid-cols-2 gap-5 reveal">
            {Object.values(MODELS).map((m) => (
              <OptionCard
                key={m.id}
                selected={model === m.id}
                onClick={() => setModel(m.id)}
                testId={`model-${m.id}`}
                badge={m.badge}
              >
                <div>
                  <div className="text-navy-deep font-display font-bold text-[22px]">
                    {m.name}
                  </div>
                  <div className="overline text-cool-grey text-[10px] mt-2">
                    {m.tag}
                  </div>
                </div>
                <div className="mt-6 space-y-1.5">
                  <div className="font-mono-data text-[13px] text-navy-deep">
                    {m.capacity}
                  </div>
                  <div className="font-mono-data text-[12px] text-cool-grey">
                    {m.spec}
                  </div>
                </div>
              </OptionCard>
            ))}
          </div>

          <div className="mt-5 grid md:grid-cols-2 gap-5 reveal">
            {Object.values(BUNDLES).map((b) => {
              const p = getPricing(model, b.id);
              return (
                <OptionCard
                  key={b.id}
                  selected={bundle === b.id}
                  onClick={() => setBundle(b.id)}
                  testId={`bundle-${b.id}`}
                  badge={b.badge}
                >
                  <div>
                    <div className="text-navy-deep font-display font-bold text-[22px]">
                      {b.name}
                    </div>
                    <div className="overline text-cool-grey text-[10px] mt-2">
                      {b.contents}
                    </div>
                  </div>
                  <div className="mt-1 text-[12.5px] text-slate-600">
                    {b.included}
                  </div>
                  <div className="mt-5 flex items-baseline gap-3">
                    <div
                      data-testid={`bundle-${b.id}-price`}
                      className="font-display font-bold text-navy-deep text-4xl tabular-nums"
                    >
                      ${p.price}
                    </div>
                    <div className="font-mono-data text-slate-400 line-through tabular-nums text-sm">
                      ${p.compareAt}
                    </div>
                    {p.perUnit && (
                      <div className="ml-auto font-mono-data text-[12px] text-teal-brand">
                        ${p.perUnit} / unit
                      </div>
                    )}
                  </div>
                </OptionCard>
              );
            })}
          </div>

          {/* Step C plug */}
          <div className="mt-10 reveal">
            <div className="overline text-cool-grey mb-3">Power plug</div>
            {bundle === "bundle" ? (
              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <div className="text-[11px] font-medium text-navy-deep mb-1.5">
                    Cooler #1 plug
                  </div>
                  <Select value={plug} onValueChange={setPlug}>
                    <SelectTrigger
                      data-testid="plug1-trigger"
                      className="h-12 rounded-full bg-white border-[#8A97A8]/40 px-5 font-semibold text-navy-deep focus:ring-2 focus:ring-teal-brand/40 focus:ring-offset-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      {PLUGS.map((p) => (
                        <SelectItem
                          key={p.id}
                          value={p.id}
                          data-testid={`plug1-option-${p.id}`}
                          className="font-medium"
                        >
                          <span className="mr-2">{p.flag}</span>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[11px] font-medium text-navy-deep mb-1.5">
                    Cooler #2 plug
                  </div>
                  <Select value={plug2} onValueChange={setPlug2}>
                    <SelectTrigger
                      data-testid="plug2-trigger"
                      className="h-12 rounded-full bg-white border-[#8A97A8]/40 px-5 font-semibold text-navy-deep focus:ring-2 focus:ring-teal-brand/40 focus:ring-offset-0"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      {PLUGS.map((p) => (
                        <SelectItem
                          key={p.id}
                          value={p.id}
                          data-testid={`plug2-option-${p.id}`}
                          className="font-medium"
                        >
                          <span className="mr-2">{p.flag}</span>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <PlugSegmented value={plug} onChange={setPlug} />
            )}
          </div>
        </div>

        {/* Sticky Total Bar */}
        <div className="mt-6 rounded-[28px] bg-white border border-[#8A97A8]/25 p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 shadow-[0_30px_80px_-40px_rgba(2,47,107,0.25)]">
          <div>
            <div className="overline text-cool-grey mb-2">Total</div>
            <div className="flex items-baseline gap-3">
              <span
                data-testid="total-price"
                className="font-display font-bold text-navy-deep text-5xl tabular-nums"
              >
                ${pricing.price}
              </span>
              <span className="font-mono-data text-slate-400 line-through tabular-nums text-lg">
                ${pricing.compareAt}
              </span>
            </div>
            <div className="mt-2 overline text-teal-brand text-[11px]">
              Save ${savings}
            </div>
          </div>
          <div className="md:ml-auto flex flex-col items-start md:items-end gap-2">
            <Button
              data-testid="buy-now-button"
              onClick={() => setOpen(true)}
              className="group relative overflow-hidden h-14 px-8 rounded-full bg-cta-gradient text-white text-[15px] font-semibold border border-white/25 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-1px_0_rgba(2,47,107,0.35),0_20px_50px_-14px_rgba(2,167,196,0.55)] hover:brightness-110 hover:-translate-y-0.5 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.75),inset_0_-1px_0_rgba(2,47,107,0.35),0_18px_40px_-14px_rgba(2,167,196,0.7),0_0_30px_-6px_rgba(1,207,198,0.55)] active:scale-[0.98] transition-all duration-300"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-3 top-0 h-1/2 rounded-full bg-gradient-to-b from-white/45 to-transparent opacity-90"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -inset-y-1 -left-1/3 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-white/55 to-transparent opacity-0 group-hover:opacity-100 group-hover:left-[110%] transition-all duration-700 ease-out"
              />
              <span className="relative z-10 inline-flex items-center">
                Buy Now{" "}
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="ml-1 transition-transform group-hover:translate-x-1"
                />
              </span>
            </Button>
            <div className="font-mono-data text-[11px] text-cool-grey uppercase tracking-widest">
              Free shipping · 2-year warranty · 14-day returns
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-[#8A97A8]/25 p-3 shadow-[0_-10px_40px_rgba(0,0,0,0.06)]">
        <div className="max-w-[1400px] mx-auto flex items-center gap-3">
          <div className="flex-1">
            <div className="overline text-cool-grey text-[9.5px]">
              {BUNDLES[bundle].name}
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-bold text-navy-deep text-xl tabular-nums">
                ${pricing.price}
              </span>
              <span className="font-mono-data text-slate-400 line-through tabular-nums text-xs">
                ${pricing.compareAt}
              </span>
            </div>
          </div>
          <Button
            data-testid="buy-now-sticky"
            onClick={() => setOpen(true)}
            className="h-12 px-6 rounded-full bg-cta-gradient text-white font-semibold"
          >
            Buy Now
          </Button>
        </div>
      </div>

      <ReservationDialog
        open={open}
        onOpenChange={setOpen}
        model={model}
        bundle={bundle}
        plug={plug}
        plug2={bundle === "bundle" ? plug2 : null}
      />
    </section>
  );
}
